import type {FilledContentRelationshipField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicLink, PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import Link from 'next/link'
import {useRouter} from 'next/router'
import {useLinkResolver} from 'prismicio'
import type {TestimonialsSectionSlicePartnershipsPrimaryTabItem} from 'prismicio-types'
import {motionImageProps, motionProps} from 'slices/TestimonialsSection/constants'
import {ButtonV2} from 'src/common/ButtonV2'
import {EVENTS, getLinkLocale, track} from 'src/common/helpers'
import {Icon} from 'src/common/Icon'

export const ContentContainer = ({
  activeTab,
  activeTabContent,
  componentIndex,
  pageUid,
  enableLargeImage = false,
  usePrimaryButton = false,
}: {
  activeTab: number
  activeTabContent: TestimonialsSectionSlicePartnershipsPrimaryTabItem
  componentIndex: number
  pageUid: string
  enableLargeImage?: boolean
  usePrimaryButton?: boolean
}) => (
  <div
    className={cn('flex flex-col gap-8 rounded-lg border border-moss-black/[0.1] bg-moss-white p-6 lg:flex-row', {
      'lg:gap-6 md:p-8': !enableLargeImage,
      'lg:gap-16 md:p-10': enableLargeImage,
    })}
  >
    <AnimatePresence mode="sync">
      <div className="flex flex-col gap-8 md:w-full md:justify-between">
        <div className="flex flex-col gap-3 md:gap-4">
          <motion.span className="text-button-s text-[#2C8FFF]" key={`activeTab-tag-${activeTab}`} {...motionProps}>
            {activeTabContent.tab_tag}
          </motion.span>
          <motion.h4
            key={`activeTab-title-${activeTab}`}
            {...motionProps}
            className={cn('text-tg-h3 text-moss-black', {
              'md:text-tg-h2': enableLargeImage,
            })}
          >
            {activeTabContent.tab_title}
          </motion.h4>
          <motion.div key={`activeTab-description-${activeTab}`} {...motionProps}>
            <PrismicRichText
              field={activeTabContent.tab_content}
              components={{
                paragraph: ({children}) => <p className="text-body-m md:text-tg-body-l text-moss-black">{children}</p>,
                list: ({children}) => (
                  <ul className="text-tg-body-m flex list-none flex-col gap-2 text-moss-grey-500">{children}</ul>
                ),
                listItem: ({children}) => (
                  <li className="flex items-start gap-3">
                    <div className="mt-[7px] h-[5px] w-[5px] min-w-[5px] rounded-[1px] bg-moss-grey-300" />
                    {children}
                  </li>
                ),
                hyperlink: ({children, node}) => (
                  <PrismicLink
                    field={node.data}
                    className="text-body-m md:text-tg-body-l text-moss-black underline underline-offset-4 hover:text-moss-black/[70%]"
                  >
                    {children}
                  </PrismicLink>
                ),
              }}
            />
          </motion.div>
        </div>
        {isFilled.link(activeTabContent.link_url) && (
          <ContentButton
            activeTab={activeTab}
            activeTabContent={activeTabContent}
            componentIndex={componentIndex}
            pageUid={pageUid}
            usePrimaryButton={usePrimaryButton}
          />
        )}
      </div>
    </AnimatePresence>
    <div
      className={cn('relative isolate flex w-full items-end overflow-hidden rounded-md p-2', {
        'aspect-[556/320]': !enableLargeImage,
        'aspect-[529/448]': enableLargeImage,
      })}
    >
      <AnimatePresence mode="sync">
        <motion.img
          className="absolute inset-0 -z-10 size-full overflow-hidden object-cover"
          key={`activeTab-image-${activeTab}`}
          src={activeTabContent.image.url || ''}
          alt={activeTabContent.image.alt || ''}
          {...motionImageProps}
        />
        {isFilled.keyText(activeTabContent.quote) && (
          <motion.div
            key={`activeTab-quote-${activeTab}`}
            {...motionProps}
            className="flex w-full items-center gap-4 rounded-[4px] border border-moss-white/[40%] bg-moss-white/[10%] p-3 backdrop-blur-[40px] md:w-fit"
          >
            <div className="flex size-8 min-h-8 min-w-8 items-center justify-center rounded-[2px] bg-moss-white">
              <Icon name="quotation-mark" className="h-1.5 w-3 text-black" />
            </div>
            <p className="text-15 font-medium leading-[19px] text-moss-white">{activeTabContent.quote}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </div>
)

const ContentButton = ({
  activeTab,
  activeTabContent,
  componentIndex,
  pageUid,
  usePrimaryButton = false,
}: {
  activeTab: number
  activeTabContent: TestimonialsSectionSlicePartnershipsPrimaryTabItem
  componentIndex: number
  pageUid: string
  usePrimaryButton?: boolean
}) => {
  const {linkResolver} = useLinkResolver()
  const {locale} = useRouter()
  const linkLocale = getLinkLocale(activeTabContent.link_url, locale)

  if (usePrimaryButton) {
    return (
      <ButtonV2
        trackingData={{
          mktgId: `testimonial-${activeTab}-button`,
          location: `component-${componentIndex}-testimonials`,
          pageName: pageUid,
          dataType: 'customer-story-button',
        }}
        variant="primary"
        href={linkResolver(activeTabContent.link_url)}
        locale={linkLocale}
        className="w-fit"
      >
        {/* BUG: `.text` isn't a field on FilledContentRelationshipField, so this always evaluates
            to undefined and the button renders with no visible label. Needs a real label source,
            e.g. a dedicated field on the tab item or reusing `quote`. */}
        {(activeTabContent.link_url as FilledContentRelationshipField & {text?: string}).text}
      </ButtonV2>
    )
  }

  return (
    <Link
      href={linkResolver(activeTabContent.link_url)}
      locale={linkLocale}
      className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
      onClick={() =>
        track({
          event: EVENTS.CLICK,
          mktgId: `testimonial-${activeTab}-button`,
          location: `component-${componentIndex}-testimonials`,
          pageName: pageUid,
          dataType: 'customer-story-button',
        })
      }
    >
      <span className="text-button-s uppercase text-black">
        {/* TODO: see the identical cast in the usePrimaryButton branch above. */}
        {(activeTabContent.link_url as FilledContentRelationshipField & {text?: string}).text}
      </span>
      <div className="flex size-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
        <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
      </div>
    </Link>
  )
}
